import {useState, useEffect, useMemo} from 'react';
import {useAtomValue} from 'jotai';
import {pageSizeRecoil} from '../recoil';
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import ActionBar from '../actionBar';
import HDMConfigModal from './hdmConfigModal';
import HDMConfigInfo from './hdmConfigInfo';
import {useSort} from '../../lib/useQueryModifiers';
import Table from '../table';
import {dateFormatter} from '../dateFormatter';
import Portal from '../portal';
import {Link} from 'react-router-dom';
import {useCustomerContext} from '../../lib/context/customerContext';

export const HDMConfig = () => {
  const [sort, setSort] = useSort();
  const [pageNum, setPageNum] = useState(1);
  const [totalRecordsState, setTotalRecordsState] = useState(0);
  const pageSize = useAtomValue(pageSizeRecoil); 
  const {hasPermission} = useCustomerContext();

  // RBAC: HDM_PROFILE_ASSIGNMENT_VIEW - "Hide the link to the HDM profile
  // assignment page (the link on the numbers in 'Assignments' column)".
  // This is a table formatter (plain function, not a component), so the
  // permission check has to happen here at the parent level, not via <Can>
  // inside the formatter itself - hooks can't be called there.
  const canViewAssignments = hasPermission('HDM_PROFILE_ASSIGNMENT_VIEW');

  const assignmentsFormatter = (cell, row) => {
    const count = row?.hdm_config_preset_device_assignments?.length ?? 0;
    if (!canViewAssignments) {
      return <>{count || "0"}</>;
    }
    return (
      <>
        {count ?
          <Link className="hdm-assignments-link" to={'/hdm/assignments'} state={{hdmId: row.id, title: row.name}} onClick={e => e.stopPropagation()}>
            {count}
          </Link>
        : "0"}
      </>
    );
  }

  const profilesFilters = useMemo(() => ({
    combineAnd: [
      ...sort,
      { field: "search.fields", value: `name` },
      { field: "with.hdm_config_preset_device_assignments", value: "1" },
      { field: "options.page.num", value: `${pageNum}` },
      { field: "options.page.len", value: `${pageSize}` }
    ]
  }), [sort, pageNum, pageSize]);

  const {data, loading} = useQuery(PROFILES, 
  {
    variables: { filters: profilesFilters },
    skip: !sort.length
  });  

  const totalQueried = data?.HDMConfigProfiles?.metadata?.page?.records ?? totalRecordsState;

  useEffect(() => {
    setTotalRecordsState(totalQueried);
  }, [totalQueried]);

  const profiles = data?.HDMConfigProfiles?.result?.reduce((acc, sub) => {
    const prof = {...sub};
    const devices = [...prof.hdm_config_preset_device_assignments];

    if(devices.length) {
      const filtered = devices?.filter((device) => device?.status === "A");
      delete prof.hdm_config_preset_device_assignments;
      prof.hdm_config_preset_device_assignments = filtered;
    }
    acc.push(prof);
    return acc;
  }, []) || [];

  const columns = [
    { dataField: 'id', hidden: true },
    { dataField: 'name', text: 'HDM Profile Name' },
    { dataField: 'assignments', text: 'Assignments', align: 'left', formatter: assignmentsFormatter },
    { dataField: 'created_at', text: 'Created (UTC)', sort: true, formatter: dateFormatter },
    { dataField: 'modified_at', text: 'Last Updated (UTC)', sort: true, formatter: dateFormatter }
  ];

  return(
    <>
      <Portal title="HDM Profile">
        <HDMConfigInfo title="Profile Information" type="hdm" />
      </Portal>
      <ActionBar showHidden={false} showSearchBar={true} showSearchInput={false} title="Manage HDM Profiles">
        <HDMConfigModal />
      </ActionBar>
      <Table
        testid="hdm-config-table"
        data={profiles}
        columns={columns}
        loading={loading}
        pageSize={pageSize}
        pageNum={pageNum}
        setPageNum={setPageNum}
        totalRecords={totalRecordsState}
        setSort={setSort}
      />
    </>
  );
};

const PROFILES = gql`
query getHDMConfigProfiles($filters: GenericFilters!) {
  HDMConfigProfiles (filters: $filters) {
    result {
      id
      name
      is_deleted
      created_at
      modified_at
      hdm_config_preset_device_assignments { id status }
    }
    metadata { page { num len total inPage records } }
  }
}
`;

export default HDMConfig;
